import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import ClassCard from './ClassCard';

type ClassData = {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  video_url: string;
  categories: string[];
  created_at: string;
};

type Category = {
  id: string;
  name: string;
};

interface ClassGridProps {
  classes: ClassData[];
  categories: Category[];
  isAdmin: boolean;
  copiedClassId: string | null;
  onCopyLink: (classId: string) => void;
  onEdit: (class_: ClassData) => void;
  onAddClass: () => void;
  onMoveToFolder?: (class_: ClassData) => void;
}

const ClassGrid = ({
  classes,
  categories,
  isAdmin,
  copiedClassId,
  onCopyLink,
  onEdit,
  onAddClass,
  onMoveToFolder
}: ClassGridProps) => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check user's motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply different animation settings based on user's motion preference
  const motionProps = {
    button: {
      whileHover: prefersReducedMotion ? {} : { 
        scale: 1.02, 
        boxShadow: '0 12px 20px -5px rgba(0, 0, 0, 0.3), 0 8px 16px -8px rgba(0, 0, 0, 0.4)' 
      },
      whileTap: prefersReducedMotion ? {} : { scale: 0.98 },
      initial: { opacity: 0, y: prefersReducedMotion ? 0 : 20 },
      animate: { opacity: 1, y: 0 },
      transition: { 
        duration: prefersReducedMotion ? 0.2 : 0.5, 
        ease: [0.33, 1, 0.68, 1]
      }
    },
    icon: {
      whileHover: prefersReducedMotion ? {} : { scale: 1.2, rotate: 90 },
      transition: { duration: 0.2, ease: [0.33, 1, 0.68, 1] }
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
      {isAdmin && (
        <motion.button
          onClick={onAddClass}
          className="bg-[#151D29] rounded-xl aspect-video flex items-center justify-center group shadow-lg"
          {...motionProps.button}
        >
          <motion.div
            {...motionProps.icon}
          >
            <Plus className="w-16 h-16 text-[#c9fffc]" />
          </motion.div>
        </motion.button>
      )}
      
      {classes.map((class_, index) => (
        <ClassCard
          key={class_.id}
          class_={class_}
          categories={categories}
          isAdmin={isAdmin}
          copiedClassId={copiedClassId}
          onCopyLink={onCopyLink}
          onEdit={onEdit}
          onMoveToFolder={onMoveToFolder}
          index={index}
        />
      ))}
    </div>
  );
};

export default ClassGrid;
